import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Shield, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn, user, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if already logged in as local admin
  useEffect(() => {
    const checkLocalAdmin = async () => {
      const token = localStorage.getItem("admin_token");
      if (token) {
        try {
          const response = await fetch(`${API_URL}/api/auth/verify-admin-token?token=${token}`);
          const data = await response.json();
          if (data.valid) {
            navigate("/admin", { replace: true });
            return;
          }
        } catch (e) {
          console.log("Token check failed:", e);
        }
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
      }
    };
    checkLocalAdmin();
  }, [navigate]);

  // If already logged in as Supabase admin, redirect
  if (!isLoading && user && isAdmin) {
    navigate("/admin", { replace: true });
    return null;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // First, try local admin login (MongoDB)
    try {
      const adminResponse = await fetch(`${API_URL}/api/auth/admin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      const adminData = await adminResponse.json();
      
      if (adminData.success && adminData.is_admin) {
        // Save admin token to localStorage
        localStorage.setItem("admin_token", adminData.token);
        localStorage.setItem("admin_user", JSON.stringify(adminData.user));
        setLoading(false);
        toast({ title: "مرحباً بك!", description: `أهلاً ${adminData.user.name}` });
        navigate("/admin", { replace: true });
        return;
      }
    } catch (e) {
      console.log("Admin login check:", e);
    }
    
    // Fall back to Supabase login
    const { error } = await signIn(email, password);
    setLoading(false);

    if (error) {
      toast({
        title: "خطأ في تسجيل الدخول",
        description: "البريد الإلكتروني أو كلمة المرور غير صحيحة",
        variant: "destructive",
      });
    } else {
      // Will be redirected by the useEffect check after auth state updates
      toast({ title: "مرحباً بك!", description: "جاري التحقق من الصلاحيات..." });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent/5 blur-[100px]" />
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md relative z-10"
      >
        <Card className="glass-card border-primary/10 rounded-2xl modern-shadow">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 p-4 rounded-2xl bg-primary/10 w-fit">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="font-amiri text-3xl gold-text">دخول لوحة التحكم</CardTitle>
            <p className="text-muted-foreground mt-2 text-sm">
              هذه الصفحة مخصصة للمشرفين فقط
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="admin-email">البريد الإلكتروني</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@email.com"
                  className="bg-secondary/50 border-primary/10 rounded-xl"
                  dir="ltr"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">كلمة المرور</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-secondary/50 border-primary/10 rounded-xl"
                  dir="ltr"
                  required
                />
              </div>
              <Button type="submit" className="w-full gold-gradient text-primary-foreground rounded-xl" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : <Shield className="h-4 w-4 ml-2" />}
                دخول لوحة التحكم
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
